import { getDashboardPipelineSummary } from "@hp/api-client";
import type { ClaimCandidateStatus } from "@hp/types";
import { CLAIM_CANDIDATES } from "../mocks/fixtures";
import { USE_MOCK_DATA } from "./config";
import { simulateNetwork } from "./simulateNetwork";
import type { DashboardPipelineSummary, ForumSummary, PipelineStageSummary } from "./types";

/** Every ClaimCandidate.status value, in pipeline order. */
export const ALL_STATUSES: ClaimCandidateStatus[] = [
  "identified",
  "validated",
  "filed",
  "allowed",
  "recovered",
  "rejected",
  "deferred",
];

/** The five stages that make up the forward-moving pipeline the CFO tracks. */
export const FORWARD_STAGES: ClaimCandidateStatus[] = [
  "identified",
  "validated",
  "filed",
  "allowed",
  "recovered",
];

/**
 * Returns the same shape as `@hp/api-client`'s `getDashboardPipelineSummary`
 * (GET /dashboard/pipeline-summary). Computed from local fixtures when
 * `USE_MOCK_DATA` is true; otherwise calls straight through to the real
 * endpoint with no shape difference to callers.
 */
export function fetchPipelineSummary(): Promise<DashboardPipelineSummary> {
  if (!USE_MOCK_DATA) return getDashboardPipelineSummary();

  const byStatus: PipelineStageSummary[] = ALL_STATUSES.map((status) => {
    const rows = CLAIM_CANDIDATES.filter((c) => c.status === status);
    return {
      status,
      count: rows.length,
      value_low_total: rows.reduce((sum, r) => sum + r.value_low, 0),
      value_high_total: rows.reduce((sum, r) => sum + r.value_high, 0),
    };
  });

  const forumCounts = new Map<string, number>();
  for (const c of CLAIM_CANDIDATES) {
    forumCounts.set(c.recommended_forum, (forumCounts.get(c.recommended_forum) ?? 0) + 1);
  }
  const byForum: ForumSummary[] = Array.from(forumCounts.entries())
    .map(([recommended_forum, count]) => ({ recommended_forum, count }))
    .sort((a, b) => b.count - a.count);

  // This app does not model WorkbenchMatter (that is the Workbench app's
  // concern), so "upcoming hearings" is proxied by candidates already filed
  // and awaiting a hearing - a reasonable stand-in grounded in this app's
  // own data rather than an invented number.
  const upcomingHearingsCount = CLAIM_CANDIDATES.filter((c) => c.status === "filed").length;

  const summary: DashboardPipelineSummary = {
    as_of: "2026-09-11T06:00:00Z",
    total_claim_candidates: CLAIM_CANDIDATES.length,
    total_value_low: CLAIM_CANDIDATES.reduce((sum, c) => sum + c.value_low, 0),
    total_value_high: CLAIM_CANDIDATES.reduce((sum, c) => sum + c.value_high, 0),
    by_status: byStatus,
    by_forum: byForum,
    upcoming_hearings_count: upcomingHearingsCount,
    limitation_flagged_count: CLAIM_CANDIDATES.filter((c) => c.limitation_flag).length,
    // Mock contracts carry no tariff ledger, so the mock treats every
    // potential claim as on a ledger-ready contract (same as the real API
    // would for a portfolio whose ledgers are all loaded).
    potential_ledger_ready: byStatus.find((s) => s.status === "identified") ?? null,
  };

  return simulateNetwork(summary);
}
